import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";
import { CSS } from "../styles";
import { ROLE_META, ROLE_LABELS, ROOMS_IDS, ROOM_ICON, ROOM_LABELS, ROOM_WEIGHTS } from "../constants";
import { formatEur, daysUntil, today } from "../utils";
import FloorPlan from "./FloorPlan";
import ClientTracking from "./ClientTracking";
import PinterestInspiration from "./PinterestInspiration";
import BottomNav from "./BottomNav";
import ConfirmModal from "./ConfirmModal";
import LoadingScreen from "./LoadingScreen";
import ContractorHome from "./ContractorHome";
import GanttChart from "./GanttChart";
import CalendarView from "./CalendarView";
import VoiceRecorder from "./VoiceRecorder";
import { useLang, LangSelector } from "../LangContext";
import { DEFAULT_TODOS, DEFAULT_MATERIALS, DEFAULT_BUDGET } from "../defaultData";

console.log("[ProjectApp] SQL à exécuter:\nALTER TABLE projects ADD COLUMN IF NOT EXISTS subcontractors jsonb DEFAULT '[]';");

function formatJournalDate(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime()) || !/[TZ]|\d{4}-\d{2}-\d{2}/.test(String(raw))) return raw;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today - dDay) / 86400000);
  const hhmm = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 0) return `Aujourd'hui à ${hhmm}`;
  if (diffDays < 7) {
    const label = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    return `${label.charAt(0).toUpperCase() + label.slice(1)} à ${hhmm}`;
  }
  return `${d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} à ${hhmm}`;
}

function applyBold(str) {
  return str.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}

function renderVoiceSummary(text) {
  const lines = (text ?? "").split("\n");
  const elements = [];
  let listItems = [];

  const flushList = (key) => {
    if (listItems.length === 0) return;
    elements.push(
      <ul key={`ul-${key}`} style={{ margin: "2px 0 8px 0", paddingLeft: 18 }}>
        {listItems.map((item, i) => (
          <li key={i} style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.6 }}>{applyBold(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) { flushList(i); return; }
    if (/^[📋⚡❓]/.test(trimmed)) {
      flushList(i);
      elements.push(
        <h4 key={i} style={{ fontSize: 11, color: "#C9A96E", fontWeight: 700, margin: "10px 0 3px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {trimmed}
        </h4>
      );
    } else if (trimmed.startsWith("•")) {
      listItems.push(trimmed.slice(1).trim());
    } else {
      flushList(i);
      elements.push(
        <p key={i} style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.6, margin: "2px 0" }}>
          {applyBold(trimmed)}
        </p>
      );
    }
  });
  flushList("end");
  return <div>{elements}</div>;
}

export default function ProjectApp({ project, profile, onBack, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [activeRoom, setActiveRoom] = useState("kitchen");
  const [saving, setSaving] = useState(false);
  const [todos, setTodos] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [budget, setBudget] = useState(null);
  const [journal, setJournal] = useState(null);
  const [members, setMembers] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [newMsg, setNewMsg] = useState(""); const [newMsgType, setNewMsgType] = useState("message"); const [newMsgCat, setNewMsgCat] = useState("material"); const [newMsgPhotos, setNewMsgPhotos] = useState([]); const [uploadingMsgPhoto, setUploadingMsgPhoto] = useState(false); const [voiceAudioUrl, setVoiceAudioUrl] = useState(null);
  const [newTodoText, setNewTodoText] = useState(""); const [newTodoDl, setNewTodoDl] = useState(""); const [newTodoImp, setNewTodoImp] = useState(""); const [newTodoAssignee, setNewTodoAssignee] = useState("client");
  const [jfRole, setJfRole] = useState("all"); const [jfType, setJfType] = useState("all");
  const [showNotifs, setShowNotifs] = useState(false); const [readNotifs, setReadNotifs] = useState(new Set());
  const [showInvite, setShowInvite] = useState(false); const [inviteEmail, setInviteEmail] = useState(""); const [inviteRole, setInviteRole] = useState("client");
  const [showHistMat, setShowHistMat] = useState(null); const [mfRole, setMfRole] = useState("all"); const [mfSt, setMfSt] = useState("all");
  const [showAddMat, setShowAddMat] = useState(false); const [newMatLabel, setNewMatLabel] = useState(""); const [newMatPrice, setNewMatPrice] = useState(""); const [newMatUrl, setNewMatUrl] = useState(""); const [newMatLot, setNewMatLot] = useState("");
  const [showAddSituation, setShowAddSituation] = useState(false); const [newSitDesc, setNewSitDesc] = useState(""); const [newSitAmt, setNewSitAmt] = useState("");
  const [showAddLot, setShowAddLot] = useState(false); const [newLotName, setNewLotName] = useState(""); const [addLotMemberId, setAddLotMemberId] = useState(null);
  const [translations, setTranslations] = useState({}); const [transId, setTransId] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };
  const [showHdMenu, setShowHdMenu] = useState(false);
  // Confirm modal state
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, danger }
  const { t } = useLang();
  // Contractor state
  const [timesheets, setTimesheets] = useState([]); const [newEmployee, setNewEmployee] = useState(""); const [newHours, setNewHours] = useState(""); const [newHoursDate, setNewHoursDate] = useState(today()); const [newHoursLot, setNewHoursLot] = useState("");
  const [reserves, setReserves] = useState([]); const [supplies, setSupplies] = useState([]);
  const [subcontractors, setSubcontractors] = useState(project.subcontractors || []);
  const [showAddSub, setShowAddSub] = useState(false);
  const [newSubName, setNewSubName] = useState(""); const [newSubActivity, setNewSubActivity] = useState(""); const [newSubStart, setNewSubStart] = useState(""); const [newSubEnd, setNewSubEnd] = useState("");
  const [newReserve, setNewReserve] = useState(""); const [newResLoc, setNewResLoc] = useState(""); const [newResSev, setNewResSev] = useState("medium"); const [newResPhotos, setNewResPhotos] = useState([]); const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newSupplyDesc, setNewSupplyDesc] = useState(""); const [newSupplyAmt, setNewSupplyAmt] = useState("");

  const DEFAULT_FUNNEL = [
    { id: 1, label: "Brief & Vision", desc: "Besoins validés, budget défini", status: "pending", pct: 0 },
    { id: 2, label: "Plans & Permis", desc: "Plans architecte + permis", status: "pending", pct: 0 },
    { id: 3, label: "Sélection Matériaux", desc: "Cuisine · SDB · Salon · Chambre", status: "pending", pct: 0 },
    { id: 4, label: "Devis & Validation", desc: "Devis entreprises, négociation, signature", status: "pending", pct: 0 },
    { id: 5, label: "Travaux", desc: "Démolition → Second œuvre → Finitions", status: "pending", pct: 0 },
    { id: 6, label: "Livraison & Punch List", desc: "Réserves, corrections, remise des clés", status: "pending", pct: 0 },
  ];
  const [funnel, setFunnel] = useState(DEFAULT_FUNNEL);

  const role = profile.role; const rm = ROLE_META[role]; const NOW = new Date().toLocaleDateString("fr-FR");

  useEffect(() => {
    const load = async () => {
      const { data: sections } = await supabase.from("project_data").select("section, data").eq("project_id", project.id);
      const found = new Set();
      if (sections) { sections.forEach(s => { found.add(s.section); if (s.section === "todos") setTodos(s.data); if (s.section === "materials") setMaterials(s.data); if (s.section === "budget") setBudget(s.data); if (s.section === "journal") setJournal(s.data.entries || []); if (s.section === "timesheets") setTimesheets(s.data.entries || []); if (s.section === "reserves") setReserves(s.data.entries || []); if (s.section === "supplies") setSupplies(s.data.entries || []); if (s.section === "funnel") setFunnel(s.data.stages || DEFAULT_FUNNEL); }); }
      // Sécurité : une section manquante en base (projet créé par un chemin qui a sauté
      // l'insert initial, ex. seed SQL direct) ne doit pas bloquer le spinner indéfiniment.
      const missing = [];
      if (!found.has("todos")) { setTodos(DEFAULT_TODOS); missing.push({ project_id: project.id, section: "todos", data: DEFAULT_TODOS }); }
      if (!found.has("materials")) { setMaterials(DEFAULT_MATERIALS); missing.push({ project_id: project.id, section: "materials", data: DEFAULT_MATERIALS }); }
      if (!found.has("budget")) { setBudget(DEFAULT_BUDGET); missing.push({ project_id: project.id, section: "budget", data: DEFAULT_BUDGET }); }
      if (!found.has("journal")) { setJournal([]); missing.push({ project_id: project.id, section: "journal", data: { entries: [] } }); }
      if (missing.length > 0) await supabase.from("project_data").insert(missing);
      const { data: mems } = await supabase.from("project_members").select("*, profiles(name, email, role, avatar)").eq("project_id", project.id);
      setMembers(mems || []); setDataLoaded(true);
    };
    load();
  }, [project.id]);

  useEffect(() => {
    const channel = supabase.channel(`project_${project.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "project_data", filter: `project_id=eq.${project.id}` }, payload => {
        const s = payload.new;
        if (s.section === "todos") setTodos(s.data);
        if (s.section === "materials") setMaterials(s.data);
        if (s.section === "budget") setBudget(s.data);
        if (s.section === "journal") setJournal(s.data.entries || []);
        if (s.section === "timesheets") setTimesheets(s.data.entries || []);
        if (s.section === "reserves") setReserves(s.data.entries || []);
        if (s.section === "supplies") setSupplies(s.data.entries || []);
        if (s.section === "funnel") setFunnel(s.data.stages || DEFAULT_FUNNEL);
      }).subscribe();
    return () => supabase.removeChannel(channel);
  }, [project.id]);

  const saveSection = useCallback(async (section, data) => {
    setSaving(true);
    const value = section === "journal" ? { entries: data } : data;
    await supabase.from("project_data").upsert({ project_id: project.id, section, data: value, updated_at: new Date().toISOString() }, { onConflict: "project_id,section" });
    setSaving(false);
  }, [project.id]);

  const updateTodos = useCallback((v) => { setTodos(v); saveSection("todos", v); }, [saveSection]);
  const updateMaterials = useCallback((v) => { setMaterials(v); saveSection("materials", v); }, [saveSection]);
  const updateBudget = useCallback((v) => { setBudget(v); saveSection("budget", v); }, [saveSection]);
  const updateJournal = useCallback((v) => { setJournal(v); saveSection("journal", v); }, [saveSection]);
  const updateTimesheets = useCallback((v) => { setTimesheets(v); saveSection("timesheets", { entries: v }); }, [saveSection]);
  const updateReserves = useCallback((v) => { setReserves(v); saveSection("reserves", { entries: v }); }, [saveSection]);
  const updateSupplies = useCallback((v) => { setSupplies(v); saveSection("supplies", { entries: v }); }, [saveSection]);
  const updateFunnel = useCallback((v) => { setFunnel(v); saveSection("funnel", { stages: v }); }, [saveSection]);

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    const { data: existing } = await supabase.from("profiles").select("id").eq("email", inviteEmail.trim().toLowerCase()).single();
    if (existing) await supabase.from("project_members").insert({ project_id: project.id, user_id: existing.id, role: inviteRole });
    else await supabase.from("project_members").insert({ project_id: project.id, user_id: null, role: inviteRole, invited_email: inviteEmail.trim().toLowerCase() });
    await fetch("https://xrjqgengmmabmsnzosnc.supabase.co/functions/v1/send-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), role: inviteRole, projectName: project.name, joinCode: project.join_code, inviterName: profile.name }),
    });
    setInviteEmail("");
    const { data: mems } = await supabase.from("project_members").select("*, profiles(name, email, role, avatar)").eq("project_id", project.id);
    setMembers(mems || []); setShowInvite(false);
    showToast("✓ Invitation envoyée");
  };

  if (!dataLoaded || !todos || !materials || !budget || !journal) return <LoadingScreen />;

  const allMats = Object.entries(materials).flatMap(([rid, ms]) => ms.map(m => ({ ...m, rid })));
  const totalEng = allMats.reduce((s, m) => s + (m.price || 0), 0);
  const budgetMats = budget.currentTotal - budget.labor;
  const isOver = totalEng > budgetMats;
  const bPct = Math.min(100, Math.round((totalEng / Math.max(1, budgetMats)) * 100));
  const totalPaid = (budget.paymentSituations || []).filter(s => s.status === "paid").reduce((s, p) => s + p.amount, 0);
  const allTF = Object.values(todos).flat();
  const urgentCnt = allTF.filter(t => !t.done && t.deadline && daysUntil(t.deadline) <= 7 && t.assignee === role).length;

  const notifs = [];
  Object.values(todos).flat().filter(t => !t.done && t.deadline && t.assignee === role).forEach(t => {
    const d = daysUntil(t.deadline);
    if (d <= 3 && d >= 0) notifs.push({ id: `t${t.id}`, text: `Urgent : "${t.text}"`, sub: `Échéance ${d === 0 ? "aujourd'hui" : `dans ${d}j`}${t.impact ? ` · ${t.impact}` : ""}`, tab: "todos", color: "#FC8181" });
    else if (d < 0) notifs.push({ id: `tl${t.id}`, text: `En retard : "${t.text}"`, sub: `${Math.abs(d)}j de retard`, tab: "todos", color: "#FC8181" });
    else if (d <= 7) notifs.push({ id: `tw${t.id}`, text: `Cette semaine : "${t.text}"`, sub: `Échéance dans ${d}j`, tab: "todos", color: "#F6AD55" });
  });
  journal.filter(e => e.type === "formal_decision" && e.decisionStatus === "pending").forEach(e => {
    notifs.push({ id: `d${e.id}`, text: "Décision en attente", sub: e.text.substring(0, 55), tab: "journal", color: "#F6AD55" });
  });
  // Material validation notifications for client
  if (role === "client") {
    journal.filter(e => e.type === "material_validation").forEach(e => {
      notifs.push({ id: `mv${e.id}`, text: e.matStatus === "validated" ? "✅ Matériau validé" : "⏳ Matériau remis en attente", sub: e.text.substring(3, 60), tab: "journal", color: e.matStatus === "validated" ? "#A8C97E" : "#F6AD55" });
    });
  }
  (reserves || []).filter(r => r.severity === "high" && r.resolved === false).forEach(r => {
    notifs.push({ id: `rv${r.id}`, text: `🚨 Réserve bloquante : ${r.text?.substring(0, 55)}`, sub: r.location || "Signalé par l'entreprise", tab: "myproject", color: "#FC8181" });
  });
  const unreadNotifs = notifs.filter(n => !readNotifs.has(n.id));

  const fjJournal = journal.filter(e => { if (jfRole !== "all" && e.role !== jfRole) return false; if (jfType !== "all" && e.type !== jfType) return false; return true; });
  const fjMats = (materials[activeRoom] || []).filter(m => { if (mfRole !== "all" && m.addedBy !== mfRole) return false; if (mfSt === "validated" && !m.validated) return false; if (mfSt === "pending" && m.validated) return false; return true; });

  const navItems = [
    { id: "dashboard", label: t("home") }, { id: "budget", label: t("budget") }, { id: "materials", label: t("materials") },
    { id: "todos", label: t("actions") }, { id: "funnel", label: t("progress") }, { id: "journal", label: t("journal") },
    ...(role !== "client" ? [{ id: "equipe", label: t("team") }, { id: "acces", label: t("clientAccess") }] : [{ id: "inspiration", label: t("inspiration") }]),
    ...(role === "contractor" ? [{ id: "mon-chantier", label: t("myProject") }] : []),
    { id: "gantt", label: "📅 Planning" },
    { id: "calendar", label: "📆 Calendrier" },
  ];

  const toggleTodo = (rid, tid) => {
    const t = todos[rid].find(t => t.id === tid);
    const n = { ...todos, [rid]: todos[rid].map(t => t.id === tid ? { ...t, done: !t.done } : t) };
    updateTodos(n);
    if (!t.done) showToast("✓ Action complétée");
  };
  const addTodo = (rid) => { if (!newTodoText.trim()) return; updateTodos({ ...todos, [rid]: [...todos[rid], { id: Date.now(), text: newTodoText, done: false, assignee: newTodoAssignee, deadline: newTodoDl || "", impact: newTodoImp || null, impactDays: 0 }] }); setNewTodoText(""); setNewTodoDl(""); setNewTodoImp(""); };

  const validateMat = (rid, mid) => {
    const mat = materials[rid].find(m => m.id === mid);
    if (!mat.validated) {
      updateMaterials({ ...materials, [rid]: materials[rid].map(m => m.id === mid ? { ...m, validated: true, history: [...(m.history || []), { action: "Validé", by: role, at: NOW }] } : m) });
      showToast("✓ Matériau validé");
      // Journal notification → visible client en temps réel
      updateJournal([{
        id: Date.now(), role, authorName: profile.name,
        time: t ? t("publishedNow") : "À l'instant",
        text: `✅ Matériau validé : ${mat.label}${mat.brand ? ` — ${mat.brand}` : ""}`,
        type: "material_validation",
        matStatus: "validated",
      }, ...journal]);
    } else {
      setConfirm({
        title: "Dé-valider ce matériau ?",
        message: `"${mat.label}" sera remis en attente de validation.`,
        danger: true,
        confirmLabel: "Dé-valider",
        onConfirm: () => {
          updateMaterials({ ...materials, [rid]: materials[rid].map(m => m.id === mid ? { ...m, validated: false, history: [...(m.history || []), { action: "Dé-validé", by: role, at: NOW }] } : m) });
          showToast("↩ Matériau dé-validé");
          // Journal notification → visible client en temps réel
          updateJournal([{
            id: Date.now(), role, authorName: profile.name,
            time: t ? t("publishedNow") : "À l'instant",
            text: `⏳ Matériau remis en attente : ${mat.label}${mat.brand ? ` — ${mat.brand}` : ""}`,
            type: "material_validation",
            matStatus: "pending",
          }, ...journal]);
          setConfirm(null);
        }
      });
    }
  };

  const uploadJournalPhoto = async (file) => {
    setUploadingMsgPhoto(true);
    const ext = file.name.split(".").pop();
    const path = `journal/${project.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("reserves-photos").upload(path, file, { contentType: file.type });
    if (error) { showToast("❌ Erreur upload photo"); setUploadingMsgPhoto(false); return null; }
    const { data: { publicUrl } } = supabase.storage.from("reserves-photos").getPublicUrl(path);
    setUploadingMsgPhoto(false);
    return publicUrl;
  };

  const publishMsg = () => { if (!newMsg.trim()) return; const now = new Date(); const time = now.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + " · " + now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); updateJournal([{ id: Date.now(), role, authorName: profile.name, time, text: newMsg, type: newMsgType, decisionStatus: newMsgType === "formal_decision" ? "pending" : null, category: newMsgType === "formal_decision" ? newMsgCat : null, photos: newMsgPhotos.length > 0 ? newMsgPhotos : null, audio_url: voiceAudioUrl || null }, ...journal]); setNewMsg(""); setNewMsgPhotos([]); setVoiceAudioUrl(null); showToast("✓ Publié"); };
  const translateMsg = async (id, text) => { const key = `${id}_en`; if (translations[key]) return; setTransId(id); try { const res = await fetch("https://xrjqgengmmabmsnzosnc.supabase.co/functions/v1/translate-message", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` }, body: JSON.stringify({ text }) }); const data = await res.json(); setTranslations(p => ({ ...p, [key]: data.translated || text })); } catch { } setTransId(null); };

  const setGlobalBudget = (total) => {
    const totalN = parseFloat(total); if (!totalN || totalN <= 0) return;
    const labor = Math.round(totalN * 0.25);
    const mats = totalN - labor;
    const rooms = {}; ROOMS_IDS.forEach(rid => { rooms[rid] = Math.round(mats * (ROOM_WEIGHTS[rid] || 0.1)); });
    updateBudget({ ...budget, initialTotal: totalN, currentTotal: totalN, labor, rooms });
  };

  const leverReserve = (id) => {
    const r = reserves.find(r => r.id === id);
    if (r.resolved) {
      updateReserves(reserves.map(r2 => r2.id === id ? { ...r2, resolved: false } : r2));
      showToast("↩ Réserve réouverte");
    } else {
      setConfirm({
        title: "Lever cette réserve ?",
        message: `"${r.description || r.text}" sera marquée comme résolue.`,
        confirmLabel: t("resolve"),
        danger: false,
        onConfirm: () => {
          updateReserves(reserves.map(r2 => r2.id === id ? { ...r2, resolved: true } : r2));
          showToast(t("reserveResolved"));
          setConfirm(null);
        }
      });
    }
  };

  const handleDeclareReserve = async () => {
    if (!newReserve.trim()) return;
    const entry = {
      id: Date.now(),
      severity: newResSev,
      text: newReserve.trim(),
      location: newResLoc || null,
      date: new Date().toLocaleDateString("fr-FR"),
      resolved: false,
      photos: newResPhotos,
    };
    const { data: fresh } = await supabase
      .from("project_data")
      .select("data")
      .eq("project_id", project.id)
      .eq("section", "reserves")
      .single();
    const existingEntries = fresh?.data?.entries || [];
    const merged = [...existingEntries, entry];
    const { error } = await supabase
      .from("project_data")
      .upsert(
        { project_id: project.id, section: "reserves", data: { entries: merged }, updated_at: new Date().toISOString() },
        { onConflict: "project_id,section" }
      );
    if (error) {
      showToast("❌ Erreur lors de l'enregistrement");
      return;
    }
    updateReserves([...reserves, entry]);
    setNewReserve("");
    setNewResLoc("");
    setNewResSev("medium");
    setNewResPhotos([]);
    showToast(t("reserveDeclared"));
  };

  const uploadReservePhoto = async (file) => {
    setUploadingPhoto(true);
    const ext = file.name.split(".").pop();
    const path = `${project.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from("reserves-photos")
      .upload(path, file, { contentType: file.type });
    if (error) {
      showToast("❌ Erreur lors de l'upload photo");
      setUploadingPhoto(false);
      return null;
    }
    const { data: { publicUrl } } = supabase.storage
      .from("reserves-photos")
      .getPublicUrl(path);
    setUploadingPhoto(false);
    return publicUrl;
  };

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: "#0F0E0C", minHeight: "100vh", color: "#E8E2D8", display: "flex", flexDirection: "column" }}>
      <style>{CSS}</style>
      <header style={{ borderBottom: "1px solid #2A2924", padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 52, position: "sticky", top: 0, background: "#0F0E0C", zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={onBack} className="btn" style={{ fontSize: 18, color: "#7A7468", padding: "0 4px" }}>←</button>
          <div style={{ width: 26, height: 26, background: "#C9A96E", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>⬡</div>
          <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 14, color: "#E8E2D8", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {saving && <div className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} />}
          {/* Desktop extras */}
          <div className="hd-extra" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: `${rm.color}20`, border: `1.5px solid ${rm.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: rm.color }}>{profile.avatar}</div>
            <span className="hd-title" style={{ fontSize: 11, color: rm.color, fontWeight: 500 }}>{ROLE_LABELS[role]}</span>
            <LangSelector />
          </div>
          {/* Mobile: notif bell + menu */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ position: "relative" }}>
              <button onClick={() => setShowNotifs(p => !p)} className="btn" style={{ fontSize: 18, position: "relative" }}>
                🔔
                {unreadNotifs.length > 0 && <span className="notif-dot">{unreadNotifs.length}</span>}
              </button>
              {showNotifs && (
                <div className="notif-panel" style={{ right: 0 }}>
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid #1F1E1A", fontSize: 11, color: "#7A7468", fontWeight: 600 }}>Notifications ({notifs.length})</div>
                  {notifs.length === 0 ? <div style={{ padding: "16px", fontSize: 12, color: "#5A5450", textAlign: "center" }}>Aucune notification</div>
                    : notifs.map(n => <div key={n.id} className="notif-item" onClick={() => { setTab(n.tab); setReadNotifs(p => new Set([...p, n.id])); setShowNotifs(false); }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: n.color || "#E8E2D8" }}>{n.text}</div>
                      {n.sub && <div style={{ fontSize: 11, color: "#7A7468", marginTop: 2 }}>{n.sub}</div>}
                    </div>)}
                </div>
              )}
            </div>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${rm.color}20`, border: `1.5px solid ${rm.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: rm.color }}>{profile.avatar}</div>
            <button className="hd-menu-btn" onClick={() => setShowHdMenu(true)}>⋯</button>
          </div>
          </div>
        </div>
      </header>

      {showHdMenu && (
        <div className="modal-overlay" onClick={() => setShowHdMenu(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#E8E2D8" }}>{profile.name}</div>
                <div style={{ fontSize: 11, color: rm.color, marginTop: 2 }}>{rm.icon} {ROLE_LABELS[role]}</div>
              </div>
              <button className="btn" onClick={() => setShowHdMenu(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#7A7468", marginBottom: 8 }}>Langue</div>
                <LangSelector />
              </div>
              <button onClick={() => { setShowHdMenu(false); onLogout(); }} className="btn"
                style={{ width: "100%", padding: "12px", borderRadius: 10, background: "#252420", border: "1px solid #2A2924", color: "#FC8181", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop nav */}
      <nav className="nav-scroll" style={{ borderBottom: "1px solid #2A2924", padding: "0 16px" }}>
        {navItems.map(nt => (
          <button key={nt.id} onClick={() => setTab(nt.id)} className={`btn nav-item${tab === nt.id ? " active" : ""}`}
            style={{ padding: "0 10px", height: 44, fontSize: 11, fontWeight: tab === nt.id ? 600 : 400, color: tab === nt.id ? "#E8E2D8" : "#7A7468", whiteSpace: "nowrap" }}>
            {nt.label}
          </button>
        ))}
      </nav>

      <main className="main-pad">

        {tab === "dashboard" && role === "client" && (
          <ClientTracking project={project} todos={todos} journal={journal} budget={budget} materials={materials} notifs={notifs} setTab={setTab} funnel={funnel} />
        )}

        {tab === "dashboard" && role === "contractor" && (
          <ContractorHome
            project={project}
            profile={profile}
            members={members}
            timesheets={timesheets}
            reserves={reserves}
            supplies={supplies}
            journal={journal}
            todos={todos}
            setTab={setTab}
            updateTimesheets={updateTimesheets}
            updateReserves={updateReserves}
            updateSupplies={updateSupplies}
            uploadReservePhoto={uploadReservePhoto}
            updateJournal={updateJournal}
            showToast={showToast}
          />
        )}

        {tab === "dashboard" && role === "architect" && (
          <div>
            <h1 className="st">{project.name}</h1>
            {project.address && <div style={{ fontSize: 12, color: "#5A5450", marginBottom: 16 }}>📍 {project.address}</div>}
            <FloorPlan projectId={project.id} />

            {/* ── Date de livraison ── */}
            <div className="card" style={{ padding: 16, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div className="sl" style={{ marginBottom: 4 }}>📅 Date de livraison</div>
                <div style={{ fontSize: 13, color: project.deadline ? "#C9A96E" : "#5A5450", fontWeight: project.deadline ? 600 : 400 }}>
                  {project.deadline
                    ? new Date(project.deadline).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
                    : "Non définie"}
                </div>
              </div>
              <input
                type="date"
                defaultValue={project.deadline || ""}
                onChange={async (e) => {
                  const newDeadline = e.target.value || null;
                  await supabase.from("projects").update({ deadline: newDeadline }).eq("id", project.id);
                  project.deadline = newDeadline;
                  showToast(newDeadline ? "✓ Date de livraison enregistrée" : "✓ Date supprimée");
                }}
                style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, background: "#252420", border: "1px solid #2A2924", color: "#E8E2D8", cursor: "pointer" }}
              />
            </div>
            {notifs.length > 0 && (
              <div className="card" style={{ padding: 14, marginBottom: 14, border: "1px solid #C9A96E30", background: "#C9A96E05" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#C9A96E", marginBottom: 10 }}>🔔 Mes actions à compléter ({notifs.length})</div>
                {notifs.map(n => (
                  <div key={n.id} onClick={() => setTab(n.tab)} style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", background: "#252420", borderRadius: 8, cursor: "pointer", marginBottom: 6 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: n.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 500 }}>{n.text}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{n.sub}</div></div>
                    <span style={{ fontSize: 10, color: "#5A5450" }}>→</span>
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const ganttBlocks = project.gantt_data || [];
              const totalDays = ganttBlocks.length > 0 ? Math.max(...ganttBlocks.map(b => (b.startDay || 1) + (b.duration || 0) - 1)) : 0;
              const elapsedDays = project.created_at ? Math.floor((Date.now() - new Date(project.created_at.slice(0, 10)).getTime()) / 86400000) : 0;
              const ganttProgress = totalDays > 0 ? Math.min(100, Math.max(0, Math.round(elapsedDays / totalDays * 100))) : 0;
              return (
            <div className="stats-grid">
              {[{ label: t("progress"), value: `${ganttProgress}%`, accent: "#C9A96E" }, { label: t("urgentActions"), value: urgentCnt, accent: urgentCnt > 0 ? "#FC8181" : rm.color }, { label: t("engagedMaterials"), value: formatEur(totalEng), accent: isOver ? "#FC8181" : "#A8C97E" }, { label: t("paidSituation"), value: formatEur(totalPaid), accent: "#7EB8C9" }].map((s, i) => (
                <div key={i} className="card" style={{ padding: 16 }}>
                  <div className="sl" style={{ marginBottom: 8 }}>{s.label}</div>
                  <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: s.accent }}>{s.value}</div>
                </div>
              ))}
            </div>
              );
            })()}
            <div className="card" style={{ padding: "11px 16px", marginBottom: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: "#7A7468" }}>Budget matériaux engagé</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: isOver ? "#FC8181" : "#E8E2D8" }}>{formatEur(totalEng)} / {formatEur(budgetMats)}</span>
              </div>
              <div className="pbar" style={{ height: 6 }}><div className="pfill" style={{ width: `${bPct}%`, background: isOver ? "#FC8181" : bPct > 80 ? "#F6AD55" : "#A8C97E" }} /></div>
            </div>
            <div className="card" style={{ padding: 18 }}>
              <div className="sl" style={{ marginBottom: 14 }}>Par pièce</div>
              <div className="rooms-grid">
                {ROOMS_IDS.map(rid => {
                  const rt = todos[rid] || []; const done = rt.filter(t => t.done).length; const pct = rt.length ? Math.round((done / rt.length) * 100) : 0;
                  const spent = (materials[rid] || []).reduce((s, m) => s + (m.price || 0), 0); const bud = budget.rooms[rid] || 0;
                  return (
                    <div key={rid} onClick={() => { setActiveRoom(rid); setTab("materials"); }} style={{ background: "#252420", border: "1px solid #2A2924", borderRadius: 10, padding: 13, cursor: "pointer" }}>
                      <div style={{ fontSize: 17, marginBottom: 7 }}>{ROOM_ICON[rid]}</div>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 7 }}>{ROOM_LABELS[rid]}</div>
                      <div className="pbar" style={{ height: 3, marginBottom: 4 }}><div className="pfill" style={{ width: `${pct}%`, background: "#C9A96E" }} /></div>
                      <div style={{ fontSize: 9, color: spent > bud ? "#FC8181" : "#5A5450" }}>{formatEur(spent)}/{formatEur(bud)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {tab === "budget" && (
          <div>
            <h1 className="st">Budget</h1>
            {role === "architect" && (
              <div className="card" style={{ padding: 20, marginBottom: 18, border: "1px solid #C9A96E30", background: "#C9A96E05" }}>
                <div className="sl" style={{ marginBottom: 12 }}>Budget global du projet</div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <input type="number" defaultValue={budget.initialTotal || ""} placeholder="ex: 45000" onBlur={e => e.target.value && setGlobalBudget(e.target.value)} style={{ paddingRight: 30, fontSize: 20, fontWeight: 700, color: "#C9A96E", fontFamily: "'DM Serif Display',serif" }} />
                    <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#7A7468" }}>€</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#5A5450", lineHeight: 1.6 }}>Répartition automatique<br />par pièce · 25% main d'œuvre</div>
                </div>
                {budget.initialTotal > 0 && (
                  <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#7A7468", background: "#252420", borderRadius: 6, padding: "3px 10px" }}>Main d'œuvre : {formatEur(budget.labor)}</span>
                    <span style={{ fontSize: 11, color: "#7A7468", background: "#252420", borderRadius: 6, padding: "3px 10px" }}>Matériaux : {formatEur(budgetMats)}</span>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18 }}>
              {[{ label: "Budget global", value: formatEur(budget.initialTotal), sub: "Montant total", color: "#7EB8C9" }, { label: "Main d'œuvre", value: formatEur(budget.labor), sub: `${budget.initialTotal ? Math.round(budget.labor / budget.initialTotal * 100) : 0}% du total`, color: "#C9A96E" }, { label: t("engagedMaterials"), value: formatEur(totalEng), sub: isOver ? "⚠ Dépassement" : `${formatEur(budgetMats - totalEng)} restant`, color: isOver ? "#FC8181" : "#A8C97E" }, ...(role !== "contractor" ? [{ label: "Acomptes versés", value: formatEur(Object.values(materials).flat().reduce((s, m) => s + (m.deposit || 0), 0)), sub: "Total des acomptes", color: "#C9A96E" }, { label: "Total payé matériaux", value: formatEur(Object.values(materials).flat().reduce((s, m) => s + (m.totalPaid || 0), 0)), sub: "Paiements complets", color: "#A8C97E" }] : [])].map((s, i) => (
                <div key={i} className="card" style={{ padding: 16 }}>
                  <div className="sl" style={{ marginBottom: 7 }}>{s.label}</div>
                  <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: s.color, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#5A5450" }}>{s.sub}</div>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding: 18, marginBottom: 14 }}>
              <div className="sl" style={{ marginBottom: 12 }}>Répartition par pièce</div>
              {ROOMS_IDS.map(rid => {
                const spent = (materials[rid] || []).reduce((s, m) => s + (m.price || 0), 0); const bud = budget.rooms[rid] || 0; const pct = bud ? Math.min(100, Math.round((spent / bud) * 100)) : 0;
                return (
                  <div key={rid} style={{ padding: "10px 13px", background: "#252420", borderRadius: 9, marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                      <span>{ROOM_ICON[rid]}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{ROOM_LABELS[rid]}</span>
                      {role === "architect" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="number" value={bud || ""} onChange={e => updateBudget({ ...budget, rooms: { ...budget.rooms, [rid]: parseFloat(e.target.value) || 0 } })} style={{ width: 90, fontSize: 12, padding: "3px 8px", textAlign: "right" }} />
                          <span style={{ fontSize: 11, color: "#5A5450" }}>€ · engagé : {formatEur(spent)}</span>
                        </div>
                      ) : <span style={{ fontSize: 12, color: spent > bud ? "#FC8181" : "#E8E2D8", fontWeight: 500 }}>{formatEur(spent)} / {formatEur(bud)}</span>}
                    </div>
                    <div className="pbar" style={{ height: 5 }}><div className="pfill" style={{ width: `${pct}%`, background: spent > bud ? "#FC8181" : pct > 80 ? "#F6AD55" : "#A8C97E" }} /></div>
                  </div>
                );
              })}
            </div>
            {role !== "contractor" && (
            <div className="card" style={{ padding: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div className="sl">Situations de paiement</div>
                <span style={{ fontSize: 11, color: "#A8C97E" }}>Payé : {formatEur(totalPaid)}</span>
              </div>
              {(budget.paymentSituations || []).map(s => (
                <div key={s.id} style={{ padding: "11px 13px", background: "#252420", borderRadius: 10, marginBottom: 9, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{s.description}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{s.date}</div></div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: s.status === "paid" ? "#A8C97E" : "#F6AD55" }}>{formatEur(s.amount)}</span>
                  <span className="badge" style={{ background: s.status === "paid" ? "#A8C97E20" : "#F6AD5520", color: s.status === "paid" ? "#A8C97E" : "#F6AD55", border: "none" }}>{s.status === "paid" ? "Payé" : "En attente"}</span>
                  {role === "architect" && s.status === "pending" && <button className="btn" onClick={() => { updateBudget({ ...budget, paymentSituations: budget.paymentSituations.map(p => p.id === s.id ? { ...p, status: "paid" } : p) }); showToast("✓ Situation marquée payée"); }} style={{ fontSize: 10, color: "#A8C97E", border: "1px solid #A8C97E40", borderRadius: 6, padding: "2px 8px" }}>Valider</button>}
                </div>
              ))}
              {role === "architect" && <button className="btn" onClick={() => setShowAddSituation(true)} style={{ width: "100%", padding: 9, border: "1px dashed #2A2924", borderRadius: 8, color: "#5A5450", fontSize: 12, marginTop: 7 }}>+ Nouvelle situation</button>}
            </div>
            )}
          </div>
        )}

        {tab === "materials" && (
          <div>
            <h1 className="st">{t("materials")}</h1>
            <div className="room-tabs">
              {ROOMS_IDS.map(rid => <button key={rid} onClick={() => { setActiveRoom(rid); setShowHistMat(null); }} className="btn rt" style={{ padding: "5px 13px", borderRadius: 8, fontSize: 12, background: activeRoom === rid ? "#C9A96E20" : "#1A1916", border: `1px solid ${activeRoom === rid ? "#C9A96E60" : "#2A2924"}`, color: activeRoom === rid ? "#C9A96E" : "#7A7468" }}>{ROOM_ICON[rid]} {ROOM_LABELS[rid]}</button>)}
            </div>
            <div className="fbar">
              {["all", "client", "architect", "contractor"].map(r => <span key={r} className={`fchip${mfRole === r ? " on" : ""}`} onClick={() => setMfRole(r)}>{r === "all" ? t("all") : ROLE_LABELS[r]}</span>)}
              <span style={{ width: 1, background: "#2A2924", alignSelf: "stretch" }} />
              {[{ v: "all", l: "Tous" }, { v: "validated", l: "Validés" }, { v: "pending", l: "En attente" }].map(s => <span key={s.v} className={`fchip${mfSt === s.v ? " on" : ""}`} onClick={() => setMfSt(s.v)}>{s.l}</span>)}
            </div>
            <div className="mats-grid">
              {fjMats.map(mat => (
                <div key={mat.id}>
                  <div style={{ background: "#1F1E1A", border: "1px solid #2A2924", borderRadius: 10, padding: 13 }}>
                    <div style={{ width: "100%", height: 65, borderRadius: 8, background: mat.color, marginBottom: 9, position: "relative" }}>
                      {mat.validated && <div style={{ position: "absolute", top: 5, right: 5, background: "#A8C97E", borderRadius: 4, padding: "1px 5px", fontSize: 9, color: "#0F0E0C", fontWeight: 700 }}>✓</div>}
                      {mat.lot && <div style={{ position: "absolute", bottom: 5, left: 5, background: "#00000060", borderRadius: 4, padding: "1px 6px", fontSize: 9, color: "#fff" }}>{mat.lot}</div>}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{mat.name || mat.label}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <span className="badge" style={{ background: "#252420", color: "#7A7468", border: "1px solid #2A2924" }}>{mat.tag}</span>
                      <span style={{ fontSize: 12, color: "#C9A96E", fontWeight: 500 }}>{formatEur(mat.price || 0)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 8 }}>Par {ROLE_LABELS[mat.addedBy]} · {mat.addedAt}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      <button className="btn" onClick={() => setShowHistMat(showHistMat === mat.id ? null : mat.id)} style={{ flex: 1, fontSize: 10, color: "#7A7468", border: "1px solid #2A2924", borderRadius: 6, padding: "3px 0" }}>Hist.({(mat.history || []).length})</button>
                      {role === "architect" && <button className="btn" onClick={() => validateMat(activeRoom, mat.id)} style={{ flex: 1, fontSize: 10, color: mat.validated ? "#FC8181" : "#A8C97E", border: `1px solid ${mat.validated ? "#FC818140" : "#A8C97E40"}`, borderRadius: 6, padding: "3px 0" }}>{mat.validated ? "Dé-valider" : "Valider"}</button>}
                      {mat.supplierUrl && <a href={mat.supplierUrl} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: 10, color: "#7EB8C9", border: "1px solid #7EB8C940", borderRadius: 6, padding: "3px 0", textAlign: "center", textDecoration: "none", display: "block" }}>🔗 Fournisseur</a>}
                    </div>
                    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 7 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 3 }}>Date de commande</div>
                        {(role === "architect" || role === "client") ? (
                          <input type="date" value={mat.orderDate || ""} onChange={e => updateMaterials({ ...materials, [activeRoom]: materials[activeRoom].map(m => m.id === mat.id ? { ...m, orderDate: e.target.value } : m) })}
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 6, color: "#E8E2D8", background: "#1A1916", border: "1px solid #2A2924", width: "100%" }} />
                        ) : mat.orderDate ? <span style={{ fontSize: 11, color: "#E8E2D8" }}>{mat.orderDate}</span> : <span style={{ fontSize: 11, color: "#3A3830" }}>—</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 3 }}>Livraison prévue</div>
                        {(role === "architect" || role === "client") ? (
                          <input type="date" value={mat.deliveryDate || ""} onChange={e => updateMaterials({ ...materials, [activeRoom]: materials[activeRoom].map(m => m.id === mat.id ? { ...m, deliveryDate: e.target.value } : m) })}
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 6, color: "#E8E2D8", background: "#1A1916", border: "1px solid #2A2924", width: "100%" }} />
                        ) : mat.deliveryDate ? <span style={{ fontSize: 11, color: "#E8E2D8" }}>{mat.deliveryDate}</span> : <span style={{ fontSize: 11, color: "#3A3830" }}>—</span>}
                        {mat.deliveryDate && project.deadline && mat.deliveryDate > project.deadline && (
                          <div style={{ marginTop: 4, background: "#2A0808", border: "1px solid #4A1010", borderRadius: 5, padding: "2px 8px", fontSize: 10, color: "#FC8181", display: "inline-block" }}>⚠ Retard livraison</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 3 }}>Acompte versé (€)</div>
                        {(role === "architect" || role === "client") ? (
                          <input type="number" value={mat.deposit ?? ""} onChange={e => updateMaterials({ ...materials, [activeRoom]: materials[activeRoom].map(m => m.id === mat.id ? { ...m, deposit: parseFloat(e.target.value) || 0 } : m) })}
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 6, color: "#C9A96E", background: "#1A1916", border: "1px solid #2A2924", width: "100%" }} />
                        ) : mat.deposit != null ? <span style={{ fontSize: 11, color: "#C9A96E" }}>{formatEur(mat.deposit)}</span> : <span style={{ fontSize: 11, color: "#3A3830" }}>—</span>}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 3 }}>Reste à payer</div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: (mat.price - (mat.deposit || 0) - (mat.totalPaid || 0)) > 0 ? "#FC8181" : "#A8C97E" }}>
                          {formatEur(mat.price - (mat.deposit || 0) - (mat.totalPaid || 0))}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 3 }}>Paiement total effectué (€)</div>
                        {(role === "architect" || role === "client") ? (
                          <input type="number" value={mat.totalPaid ?? ""} onChange={e => updateMaterials({ ...materials, [activeRoom]: materials[activeRoom].map(m => m.id === mat.id ? { ...m, totalPaid: parseFloat(e.target.value) || 0 } : m) })}
                            style={{ fontSize: 11, padding: "3px 7px", borderRadius: 6, color: "#A8C97E", background: "#1A1916", border: "1px solid #2A2924", width: "100%" }} />
                        ) : mat.totalPaid != null ? <span style={{ fontSize: 11, color: "#A8C97E" }}>{formatEur(mat.totalPaid)}</span> : <span style={{ fontSize: 11, color: "#3A3830" }}>—</span>}
                        {mat.totalPaid != null && mat.price > 0 && mat.totalPaid >= mat.price && (
                          <div style={{ marginTop: 4, background: "#0A1F0A", border: "1px solid #1A4A1A", borderRadius: 5, padding: "2px 8px", fontSize: 10, color: "#A8C97E", display: "inline-block" }}>✓ Soldé</div>
                        )}
                      </div>
                    </div>
                  </div>
                  {showHistMat === mat.id && (
                    <div style={{ background: "#141410", border: "1px solid #2A2924", borderRadius: 8, padding: 10, marginTop: 3 }}>
                      {(mat.history || []).map((h, i) => (
                        <div key={i} style={{ display: "flex", gap: 7, padding: "4px 0", borderBottom: "1px solid #1F1E1A", fontSize: 11 }}>
                          <span style={{ color: ROLE_META[h.by]?.color || "#7A7468", fontWeight: 600 }}>{h.action}</span>
                          <span style={{ color: "#5A5450" }}>par {ROLE_LABELS[h.by]}</span>
                          <span style={{ color: "#3A3830", marginLeft: "auto" }}>{h.at}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div onClick={() => setShowAddMat(true)} style={{ background: "#1F1E1A", border: "1px dashed #2A2924", borderRadius: 10, padding: 13, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 7, minHeight: 110, color: "#5A5450", fontSize: 12, cursor: "pointer" }}>
                <span style={{ fontSize: 18 }}>+</span>Ajouter
              </div>
            </div>
          </div>
        )}

        {tab === "todos" && (
          <div>
            <h1 className="st">To-do</h1>
            <div className="room-tabs">
              {ROOMS_IDS.map(rid => {
                const pending = (todos[rid] || []).filter(t => !t.done).length;
                const urgent = (todos[rid] || []).filter(t => !t.done && t.deadline && daysUntil(t.deadline) <= 7).length;
                return (
                  <button key={rid} onClick={() => setActiveRoom(rid)} className="btn rt"
                    style={{ padding: "5px 13px", borderRadius: 8, fontSize: 12, background: activeRoom === rid ? "#C9A96E20" : "#1A1916", border: `1px solid ${activeRoom === rid ? "#C9A96E60" : urgent > 0 ? "#FC818140" : "#2A2924"}`, color: activeRoom === rid ? "#C9A96E" : urgent > 0 ? "#F6AD55" : "#7A7468", display: "flex", alignItems: "center", gap: 5 }}>
                    {ROOM_ICON[rid]} {ROOM_LABELS[rid]}
                    {pending > 0 && <span style={{ background: urgent > 0 ? "#F6AD55" : "#C9A96E", color: "#0F0E0C", borderRadius: 99, padding: "1px 5px", fontSize: 9, fontWeight: 700 }}>{pending}</span>}
                  </button>
                );
              })}
            </div>
            <div className="card" style={{ padding: 7 }}>
              {(todos[activeRoom] || []).map(todo => {
                const ar = ROLE_META[todo.assignee]; const days = todo.deadline ? daysUntil(todo.deadline) : null;
                const dlColor = days === null ? "#5A5450" : days < 0 ? "#FC8181" : days <= 3 ? "#FC8181" : days <= 7 ? "#F6AD55" : "#A8C97E";
                const dlLabel = days === null ? "" : days < 0 ? `${Math.abs(days)}j retard` : days === 0 ? "Auj." : `J-${days}`;
                return (
                  <div key={todo.id} style={{ padding: "10px 12px", borderRadius: 8, borderBottom: "1px solid #1F1E1A" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                      <button onClick={() => toggleTodo(activeRoom, todo.id)} className="btn" style={{ width: 44, height: 44, minWidth: 44, marginLeft: -12, borderRadius: 8, border: "none", background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${todo.done ? "#C9A96E" : "#3A3830"}`, background: todo.done ? "#C9A96E" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F0E0C", fontSize: 11 }}>{todo.done && "✓"}</span>
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, textDecoration: todo.done ? "line-through" : "none", color: todo.done ? "#5A5450" : "#E8E2D8", marginBottom: 5 }}>{todo.text}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                          {role === "architect" && !todo.done ? (
                            <select value={todo.assignee} onChange={e => { const n = { ...todos, [activeRoom]: todos[activeRoom].map(t => t.id === todo.id ? { ...t, assignee: e.target.value } : t) }; updateTodos(n); }}
                              style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, color: ROLE_META[todo.assignee]?.color, background: `${ROLE_META[todo.assignee]?.color}18`, border: `1px solid ${ROLE_META[todo.assignee]?.color}40`, width: "auto" }}>
                              <option value="client">◆ Client</option>
                              <option value="contractor">● Entreprise</option>
                              <option value="architect">▲ Architecte</option>
                            </select>
                          ) : <span className="chip" style={{ color: ar?.color }}>{ar?.icon} {ROLE_LABELS[todo.assignee] || todo.assignee}</span>}
                          {role === "architect" && !todo.done ? (
                            <input type="date" value={todo.deadline || ""} onChange={e => { const n = { ...todos, [activeRoom]: todos[activeRoom].map(t => t.id === todo.id ? { ...t, deadline: e.target.value } : t) }; updateTodos(n); }}
                              style={{ fontSize: 10, padding: "2px 6px", width: "auto", borderRadius: 6, color: dlColor, background: "#1A1916", border: "1px solid #2A2924" }} />
                          ) : todo.deadline && !todo.done && <span style={{ fontSize: 10, fontWeight: 600, color: dlColor }}>📅 {dlLabel}</span>}
                        </div>
                        {!todo.done && todo.deadline && days !== null && days <= 7 && (
                          <div style={{ background: days <= 3 ? "#2A0808" : "#2A1A08", border: `1px solid ${days <= 3 ? "#4A1010" : "#4A2A10"}`, borderRadius: 6, padding: "5px 10px", fontSize: 11, color: days <= 3 ? "#FC8181" : "#F6AD55", marginTop: 6 }}>
                            🔔 {days <= 0 ? "En retard" : `Échéance dans ${days}j`} — assigné à {ROLE_LABELS[todo.assignee]}{todo.impact ? ` · ⚠ ${todo.impact}` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {role === "architect" && (
                <div style={{ padding: "14px 12px", borderTop: "1px solid #2A2924" }}>
                  <div className="sl" style={{ marginBottom: 10 }}>Nouvelle action</div>
                  <input value={newTodoText} onChange={e => setNewTodoText(e.target.value)} placeholder="Description de l'action..." style={{ marginBottom: 10, fontSize: 13 }} />
                  <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div className="sl" style={{ marginBottom: 6 }}>Assigner à</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[{ v: "client", l: "◆ Client" }, { v: "contractor", l: "● Entreprise" }, { v: "architect", l: "▲ Moi" }].map(r => (
                          <button key={r.v} onClick={() => setNewTodoAssignee(r.v)} className="btn"
                            style={{ flex: 1, padding: "6px 4px", borderRadius: 7, fontSize: 10, fontWeight: newTodoAssignee === r.v ? 600 : 400, background: newTodoAssignee === r.v ? `${ROLE_META[r.v].color}20` : "#252420", border: `1px solid ${newTodoAssignee === r.v ? ROLE_META[r.v].color + "60" : "#2A2924"}`, color: newTodoAssignee === r.v ? ROLE_META[r.v].color : "#7A7468" }}>
                            {r.l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="sl" style={{ marginBottom: 6 }}>Date limite</div>
                      <input type="date" value={newTodoDl} onChange={e => setNewTodoDl(e.target.value)} style={{ fontSize: 11, padding: "5px 8px" }} />
                    </div>
                  </div>
                  <input value={newTodoImp} onChange={e => setNewTodoImp(e.target.value)} placeholder="Impact si retard (optionnel)..." style={{ marginBottom: 10, fontSize: 11 }} />
                  <button onClick={() => addTodo(activeRoom)} className="btn" style={{ width: "100%", background: "#C9A96E", color: "#0F0E0C", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, border: "none" }}>+ Ajouter l'action</button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "funnel" && (
          <div>
            <h1 className="st">{t("progress")}</h1>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {funnel.map((stage, i) => {
                const isDone = stage.status === "done";
                const isActive = stage.status === "active";
                const borderColor = isDone ? "#C9A96E" : isActive ? "#7EB8C9" : "#2A2924";
                const badgeColor = isDone ? "#C9A96E" : isActive ? "#7EB8C9" : "#3A3830";
                const badgeBg = isDone ? "#C9A96E20" : isActive ? "#7EB8C920" : "#1F1E1A";
                const badgeLabel = isDone ? "Terminé" : isActive ? "En cours" : "À venir";
                return (
                  <div key={stage.id} className="card" style={{ padding: 14, borderLeft: `3px solid ${borderColor}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 9 }}>
                      <div style={{ width: 24, height: 24, minWidth: 24, borderRadius: "50%", border: `1.5px solid ${borderColor}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: borderColor }}>
                        {isDone ? "✓" : i + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{stage.label}</div>
                        <div style={{ fontSize: 11, color: "#5A5450", marginTop: 1 }}>{stage.desc}</div>
                      </div>
                      <span className="badge" style={{ background: badgeBg, color: badgeColor, border: "none" }}>{badgeLabel}</span>
                    </div>
                    <div className="pbar" style={{ height: 4, marginBottom: role === "architect" ? 12 : 0 }}>
                      <div className="pfill" style={{ width: `${stage.pct}%`, background: isDone ? "#C9A96E" : "#7EB8C9" }} />
                    </div>
                    {role !== "architect" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 10, color: isDone ? "#C9A96E" : isActive ? "#7EB8C9" : "#3A3830", fontWeight: 600 }}>{stage.pct}%</span>
                      </div>
                    )}
                    {role === "architect" && (
                      <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                        <input type="range" min={0} max={100} value={stage.pct}
                          onChange={e => {
                            const newPct = parseInt(e.target.value);
                            const newStatus = newPct === 100 ? "done" : newPct > 0 ? "active" : "pending";
                            updateFunnel(funnel.map(s => s.id === stage.id ? { ...s, pct: newPct, status: newStatus } : s));
                          }}
                          style={{ flex: 1, accentColor: "#C9A96E" }} />
                        <span style={{ fontSize: 11, color: "#7A7468", minWidth: 32 }}>{stage.pct}%</span>
                        <button className="btn" onClick={() => {
                          const newStatus = isDone ? "pending" : isActive ? "done" : "active";
                          const newPct = newStatus === "done" ? 100 : newStatus === "active" ? stage.pct : 0;
                          updateFunnel(funnel.map(s => s.id === stage.id ? { ...s, status: newStatus, pct: newPct } : s));
                          showToast(newStatus === "done" ? "✓ Étape terminée" : newStatus === "active" ? "▶ Étape en cours" : "↩ Étape réinitialisée");
                        }} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 7, border: `1px solid ${borderColor}`, background: `${borderColor}15`, color: borderColor, flexShrink: 0 }}>
                          {isDone ? "↩ Réouvrir" : isActive ? "✓ Terminer" : "▶ Démarrer"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "journal" && (
          <div>
            <h1 className="st">Journal</h1>
            {project.join_code && (
              <div style={{ margin: "0 0 12px", padding: "8px 12px", background: "#1A1916", borderRadius: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 13, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>📧</span>
                <span style={{ fontSize: 11, color: "#5A5450", lineHeight: 1.5 }}>
                  Mettez <span style={{ color: "#7A6848", fontFamily: "monospace" }}>{project.join_code}@{process.env.REACT_APP_EMAIL_DOMAIN || "travea.io"}</span> en copie de vos emails pour les archiver automatiquement dans ce journal.
                </span>
              </div>
            )}
            <div className="fbar">
              {["all", "client", "architect", "contractor"].map(r => <span key={r} className={`fchip${jfRole === r ? " on" : ""}`} onClick={() => setJfRole(r)}>{r === "all" ? t("all") : ROLE_LABELS[r]}</span>)}
              <span style={{ width: 1, background: "#2A2924", alignSelf: "stretch" }} />
              {[{ v: "all", l: "Tout" }, { v: "message", l: "Message" }, { v: "site_meeting", l: "🏗 Réunion" }, { v: "document", l: "Document" }, { v: "formal_decision", l: "Décision" }, { v: "pinterest", l: "📌 Pinterest" }].map(t => <span key={t.v} className={`fchip${jfType === t.v ? " on" : ""}`} onClick={() => setJfType(t.v)}>{t.l}</span>)}
            </div>
            <div className="journal-grid">
              <div>
                {fjJournal.map(e => {
                  if (e.type === "email") {
                    const rawSummary = typeof e.summary === "string" ? e.summary : JSON.stringify(e.summary ?? "");
                    const cleaned = rawSummary.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();
                    let summaryObj; try { summaryObj = JSON.parse(cleaned); } catch { summaryObj = { resume: cleaned }; }
                    const resume = String(summaryObj.resume ?? "");
                    const points = Array.isArray(summaryObj.points_cles) ? summaryObj.points_cles : [];
                    const time = formatJournalDate(e.receivedAt || e.time || e.date);
                    return (
                      <div key={e.id} className="uc" style={{ borderColor: "#7EB8C9" }}>
                        <div style={{ display: "flex", gap: 7, marginBottom: 7, alignItems: "center", flexWrap: "wrap" }}>
                          <span className="badge" style={{ background: "#7EB8C920", color: "#7EB8C9", border: "none" }}>📧 Email</span>
                          <span style={{ fontSize: 11, color: "#7A7468" }}>{e.from}</span>
                          <span style={{ fontSize: 10, color: "#5A5450", marginLeft: "auto" }}>{time}</span>
                        </div>
                        <p style={{ fontSize: 12, color: "#7A6848", fontWeight: 500, marginBottom: (resume || points.length) ? 4 : 0 }}>{e.subject}</p>
                        {resume && <p style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.6 }}>{resume}</p>}
                        {points.length > 0 && (
                          <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                            {points.map((pt, i) => <li key={i} style={{ fontSize: 12, color: "#7A7468", lineHeight: 1.6 }}>{String(pt)}</li>)}
                          </ul>
                        )}
                      </div>
                    );
                  }
                  const r = ROLE_META[e.role]; const tkey = `${e.id}_en`; const translated = translations[tkey];
                  return (
                    <div key={e.id} className="uc" style={{ borderColor: e.type === "formal_decision" ? "#7EB8C9" : e.type === "document" ? "#A8C97E" : e.type === "pinterest" ? "#E60000" : e.type === "material_validation" ? (e.matStatus === "validated" ? "#A8C97E" : "#F6AD55") : e.type === "site_meeting" ? "#C9A96E" : (r?.color ?? "#5A5450") }}>
                      <div style={{ display: "flex", gap: 7, marginBottom: 7, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: r?.color ?? "#5A5450", fontWeight: 600 }}>{r?.icon} {e.authorName || ROLE_LABELS[e.role]}</span>
                        {e.type === "site_meeting" && <span className="badge" style={{ background: "#C9A96E20", color: "#C9A96E", border: "none" }}>🏗 Réunion chantier</span>}
                        {e.type === "formal_decision" && <span className="badge" style={{ background: e.decisionStatus === "validated" ? "#A8C97E20" : "#F6AD5520", color: e.decisionStatus === "validated" ? "#A8C97E" : "#F6AD55", border: "none" }}>{e.category === "material" ? "🧱" : "🏗"} {e.decisionStatus === "validated" ? "Validée" : "En attente"}</span>}
                        {e.type === "document" && <span className="badge" style={{ background: "#A8C97E20", color: "#A8C97E", border: "none" }}>📄</span>}
                        {e.type === "pinterest" && <span className="badge" style={{ background: "#E6000015", color: "#E60000", border: "none" }}>📌 Pinterest</span>}
                        {e.type === "material_validation" && <span className="badge" style={{ background: e.matStatus === "validated" ? "#A8C97E20" : "#F6AD5520", color: e.matStatus === "validated" ? "#A8C97E" : "#F6AD55", border: "none" }}>🧱 {e.matStatus === "validated" ? "Validé" : "En attente"}</span>}
                        <span style={{ fontSize: 10, color: "#5A5450", marginLeft: "auto" }}>{formatJournalDate(e.time || e.date)}</span>
                      </div>
                      {e.audio_url
                        ? renderVoiceSummary(translated || e.content || e.text)
                        : <p style={{ fontSize: 13, color: "#B0AA9E", lineHeight: 1.6 }}>{translated || e.content || e.text}</p>
                      }
                      {e.photos && e.photos.length > 0 && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                          {e.photos.map((url, i) => (
                            <img key={i} src={url} alt="" onClick={() => window.open(url, "_blank")}
                              style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1px solid #2A2924" }} />
                          ))}
                        </div>
                      )}
                      {e.audio_url && (
                        <div style={{ marginTop: 9 }}>
                          <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 4 }}>🎤 Mémo vocal</div>
                          <audio controls src={e.audio_url} style={{ width: "100%", height: 32 }} />
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 7, marginTop: 7 }}>
                        {!translated && <button className="btn" onClick={() => translateMsg(e.id, e.content || e.text)} style={{ fontSize: 10, color: "#7EB8C9", border: "1px solid #7EB8C930", borderRadius: 6, padding: "2px 9px" }}>{transId === e.id ? "..." : "🌐 EN"}</button>}
                        {e.type === "formal_decision" && e.decisionStatus === "pending" && role === "architect" && <button className="btn" onClick={() => { updateJournal(journal.map(je => je.id === e.id ? { ...je, decisionStatus: "validated" } : je)); showToast("✓ Décision validée"); }} style={{ fontSize: 10, color: "#A8C97E", border: "1px solid #A8C97E40", borderRadius: 6, padding: "2px 9px" }}>✓ Valider</button>}
                      </div>
                    </div>
                  );
                })}
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ display: "flex", gap: 7, marginBottom: 9, flexWrap: "wrap" }}>
                    {[{ v: "message", l: "💬", label: "Message" }, { v: "site_meeting", l: "🏗", label: "Réunion chantier" }, { v: "document", l: "📄", label: "Document" }, { v: "formal_decision", l: "✍", label: "Décision" }].map(tp => (
                      <button key={tp.v} onClick={() => setNewMsgType(tp.v)} className="btn"
                        style={{ fontSize: 12, padding: "5px 12px", borderRadius: 99, background: newMsgType === tp.v ? "#C9A96E20" : "#252420", border: `1px solid ${newMsgType === tp.v ? "#C9A96E60" : "#2A2924"}`, color: newMsgType === tp.v ? "#C9A96E" : "#7A7468", display: "flex", alignItems: "center", gap: 5 }}>
                        <span>{tp.l}</span><span style={{ fontSize: 11 }}>{tp.label}</span>
                      </button>
                    ))}
                    {newMsgType === "formal_decision" && [{ v: "material", l: "🧱 Mat." }, { v: "space", l: "🏗 Amén." }].map(c => (
                      <button key={c.v} onClick={() => setNewMsgCat(c.v)} className="btn" style={{ fontSize: 11, padding: "5px 10px", borderRadius: 99, background: newMsgCat === c.v ? "#7EB8C920" : "#252420", border: `1px solid ${newMsgCat === c.v ? "#7EB8C960" : "#2A2924"}`, color: newMsgCat === c.v ? "#7EB8C9" : "#7A7468" }}>{c.l}</button>
                    ))}
                  </div>
                  {newMsgType === "site_meeting" && (
                    <div style={{ background: "#C9A96E10", border: "1px solid #C9A96E30", borderRadius: 8, padding: "8px 12px", marginBottom: 9, fontSize: 11, color: "#C9A96E" }}>
                      🏗 Réunion de chantier sur site — cette entrée sera mise en avant dans le journal
                    </div>
                  )}
                  <VoiceRecorder
                    userId={profile.id}
                    projectName={project.name}
                    onTranscription={(summary, audioUrl) => { setNewMsg(summary); setVoiceAudioUrl(audioUrl); }}
                  />
                  {voiceAudioUrl && (
                    <div style={{ marginBottom: 9, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#C9A96E" }}>🎤 Mémo joint</span>
                      <button className="btn" onClick={() => setVoiceAudioUrl(null)} style={{ fontSize: 10, color: "#FC8181", border: "1px solid #FC818130", borderRadius: 5, padding: "1px 7px" }}>✕</button>
                    </div>
                  )}
                  <textarea rows={3} value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder={newMsgType === "site_meeting" ? "Points abordés, décisions prises, présents..." : "Décision, observation, blocage..."} style={{ resize: "vertical", minHeight: "80px", fontSize: 13, marginBottom: 9 }} />
                  <div style={{ display: "flex", gap: 8, marginBottom: 9 }}>
                    <label style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "9px 12px", background: "#1A1916", borderRadius: 8, border: "1px solid #2A2924", fontSize: 12, color: uploadingMsgPhoto ? "#7A7468" : "#7EB8C9", cursor: uploadingMsgPhoto ? "default" : "pointer" }}>
                      📷 Appareil photo
                      <input type="file" accept="image/*" capture="environment" disabled={uploadingMsgPhoto} style={{ display: "none" }}
                        onChange={async (e) => {
                          const f = e.target.files[0]; if (!f) return;
                          const url = await uploadJournalPhoto(f); if (url) setNewMsgPhotos(prev => [...prev, url]);
                        }} />
                    </label>
                    <label style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "9px 12px", background: "#1A1916", borderRadius: 8, border: `1px solid ${newMsgPhotos.length > 0 ? "#7EB8C940" : "#2A2924"}`, fontSize: 12, color: uploadingMsgPhoto ? "#7A7468" : newMsgPhotos.length > 0 ? "#7EB8C9" : "#5A5450", cursor: uploadingMsgPhoto ? "default" : "pointer" }}>
                      🖼 Galerie{newMsgPhotos.length > 0 ? ` (${newMsgPhotos.length})` : ""}
                      <input type="file" accept="image/*" multiple disabled={uploadingMsgPhoto} style={{ display: "none" }}
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          for (const f of files) { const url = await uploadJournalPhoto(f); if (url) setNewMsgPhotos(prev => [...prev, url]); }
                        }} />
                    </label>
                  </div>
                  {newMsgPhotos.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 9 }}>
                      {newMsgPhotos.map((url, i) => (
                        <div key={i} style={{ position: "relative" }}>
                          <img src={url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 7, border: "1px solid #2A2924" }} />
                          <button className="btn" onClick={() => setNewMsgPhotos(prev => prev.filter((_, j) => j !== i))}
                            style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: "#FC8181", color: "#0F0E0C", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", border: "none", padding: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className="btn" onClick={publishMsg} style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600, float: "right", border: "none" }}>Publier</button>
                </div>
              </div>
              <div className="dp">
                <div className="card" style={{ padding: 18 }}>
                  <div className="sl" style={{ marginBottom: 11 }}>Décisions validées</div>
                  {journal.filter(e => e.type === "formal_decision" && e.decisionStatus === "validated").map(e => (
                    <div key={e.id} style={{ padding: "7px 0", borderBottom: "1px solid #1F1E1A" }}>
                      <div style={{ fontSize: 11, fontWeight: 500, lineHeight: 1.4, marginBottom: 2 }}>{(e.text || "").substring(0, 55)}{(e.text || "").length > 55 ? "..." : ""}</div>
                      <div style={{ fontSize: 10, color: "#5A5450" }}>{e.category === "material" ? "🧱" : "🏗"} {e.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "equipe" && role !== "client" && (
          <div>
            <h1 className="st">{t("teamTitle")}</h1>
            <div style={{ background: "#1A1208", border: "1px solid #2A2010", borderRadius: 10, padding: "10px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#7A6848" }}>Assignez les lots à chaque entreprise. Les lots apparaissent sur les matériaux correspondants.</div>
            </div>
            {members.filter(m => m.role === "contractor" && m.profiles).length === 0 ? (
              <div className="card" style={{ padding: 32, textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>●</div>
                <div style={{ fontSize: 14, color: "#7A7468", marginBottom: 6 }}>Aucune entreprise sur ce projet</div>
                <div style={{ fontSize: 12, color: "#5A5450" }}>Invitez des entreprises via l'onglet "Accès client"</div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
                {members.filter(m => m.role === "contractor" && m.profiles).map(m => {
                  const p = m.profiles; const mrc = ROLE_META["contractor"];
                  return (
                    <div key={m.id} className="card" style={{ padding: 18 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${mrc.color}20`, border: `1.5px solid ${mrc.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: mrc.color, flexShrink: 0 }}>{p.avatar}</div>
                        <div><div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{p.email}</div></div>
                      </div>
                      <div className="sl" style={{ marginBottom: 8 }}>{t("assignedLots")}</div>
                      {(m.lots || []).length === 0 ? <div style={{ fontSize: 11, color: "#3A3830", marginBottom: 8 }}>Aucun lot assigné</div> : (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                          {(m.lots || []).map(lot => (
                            <span key={lot} onClick={() => setMembers(prev => prev.map(pm => pm.id === m.id ? { ...pm, lots: (pm.lots || []).filter(l => l !== lot) } : pm))}
                              style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: `${mrc.color}20`, color: mrc.color, border: `1px solid ${mrc.color}40`, cursor: "pointer" }}>
                              {lot} ✕
                            </span>
                          ))}
                        </div>
                      )}
                      {role === "architect" && <button className="btn" onClick={() => { setAddLotMemberId(m.id); setNewLotName(""); setShowAddLot(true); }} style={{ width: "100%", fontSize: 11, color: "#5A5450", border: "1px dashed #2A2924", borderRadius: 6, padding: "5px 0" }}>+ Ajouter un lot</button>}
                    </div>
                  );
                })}
              </div>
            )}

            {role === "contractor" && (
              <div style={{ marginTop: 32 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div className="sl">Sous-traitants</div>
                  <button onClick={() => setShowAddSub(p => !p)} className="btn"
                    style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, background: "#C9A96E", color: "#0F0E0C", border: "none", fontWeight: 600 }}>
                    + Ajouter un sous-traitant
                  </button>
                </div>

                {showAddSub && (
                  <div className="card" style={{ padding: 16, marginBottom: 14 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 4 }}>Nom de l'entreprise</div>
                        <input value={newSubName} onChange={e => setNewSubName(e.target.value)} placeholder="Ex: Plomberie Martin" style={{ fontSize: 12 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 4 }}>Activité</div>
                        <input value={newSubActivity} onChange={e => {
                          const val = e.target.value;
                          console.log('[Sub] gantt_data:', project.gantt_data, 'val:', val);
                          setNewSubActivity(val);
                          const gantt = project.gantt_data || [];
                          const match = gantt.find(b => b.label.toLowerCase() === val.trim().toLowerCase());
                          if (match && project.created_at) {
                            const base = new Date(project.created_at.slice(0, 10));
                            const toISO = (d) => d.toISOString().slice(0, 10);
                            const start = new Date(base); start.setDate(base.getDate() + match.startDay - 1);
                            const end   = new Date(base); end.setDate(base.getDate() + match.startDay + match.duration - 2);
                            setNewSubStart(toISO(start));
                            setNewSubEnd(toISO(end));
                          }
                        }} placeholder="Ex: Plomberie" style={{ fontSize: 12 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 4 }}>Date de début</div>
                        <input type="date" value={newSubStart} onChange={e => setNewSubStart(e.target.value)} style={{ fontSize: 12 }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#5A5450", marginBottom: 4 }}>Date de fin</div>
                        <input type="date" value={newSubEnd} onChange={e => setNewSubEnd(e.target.value)} style={{ fontSize: 12 }} />
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                      <button onClick={() => setShowAddSub(false)} className="btn" style={{ fontSize: 12, padding: "6px 12px", color: "#5A5450" }}>Annuler</button>
                      <button className="btn" style={{ fontSize: 12, padding: "6px 14px", background: "#C9A96E", color: "#0F0E0C", border: "none", borderRadius: 7, fontWeight: 600 }}
                        onClick={async () => {
                          if (!newSubName.trim()) return;
                          const next = [...subcontractors, { id: Date.now(), name: newSubName.trim(), activity: newSubActivity.trim(), startDate: newSubStart, endDate: newSubEnd }];
                          setSubcontractors(next);
                          await supabase.from("projects").update({ subcontractors: next }).eq("id", project.id);
                          setNewSubName(""); setNewSubActivity(""); setNewSubStart(""); setNewSubEnd("");
                          setShowAddSub(false);
                        }}>
                        Ajouter
                      </button>
                    </div>
                  </div>
                )}

                {subcontractors.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px 0", color: "#5A5450", fontSize: 12 }}>Aucun sous-traitant ajouté</div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
                    {subcontractors.map(s => (
                      <div key={s.id} className="card" style={{ padding: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#E8E2D8" }}>{s.name}</div>
                            {s.activity && <div style={{ fontSize: 11, color: "#C9A96E", marginTop: 2 }}>{s.activity}</div>}
                          </div>
                          <button className="btn" onClick={async () => {
                            const next = subcontractors.filter(x => x.id !== s.id);
                            setSubcontractors(next);
                            await supabase.from("projects").update({ subcontractors: next }).eq("id", project.id);
                          }} style={{ fontSize: 11, color: "#FC8181", border: "1px solid #FC818130", borderRadius: 5, padding: "2px 8px", flexShrink: 0 }}>✕</button>
                        </div>
                        <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#7A7468" }}>
                          {s.startDate && <div>📅 Début : <span style={{ color: "#B0AA9E" }}>{new Date(s.startDate).toLocaleDateString("fr-FR")}</span></div>}
                          {s.endDate && <div>🏁 Fin : <span style={{ color: "#B0AA9E" }}>{new Date(s.endDate).toLocaleDateString("fr-FR")}</span></div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "acces" && role !== "client" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h1 className="st" style={{ marginBottom: 0 }}>{t("accessTitle")}</h1>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { }} className="btn" style={{ background: "#252420", color: "#C9A96E", border: "1px solid #C9A96E40", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 600 }}>Code : <strong>{project.join_code}</strong></button>
                <button onClick={() => setShowInvite(true)} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 12, fontWeight: 600 }}>+ Inviter</button>
              </div>
            </div>
            <div style={{ background: "#1A1208", border: "1px solid #2A2010", borderRadius: 10, padding: "10px 16px", marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: "#7A6848" }}>Code d'accès : <strong style={{ color: "#C9A96E", letterSpacing: "0.15em" }}>{project.join_code}</strong> — Partagez à vos clients et entreprises</div>
            </div>
            <div className="card" style={{ padding: 18 }}>
              <div className="sl" style={{ marginBottom: 14 }}>Membres ({members.length})</div>
              {members.length === 0 ? <div style={{ textAlign: "center", padding: "24px 0", color: "#5A5450", fontSize: 12 }}>Aucun membre — utilisez + Inviter ou partagez le code</div>
                : members.map(m => {
                  const p = m.profiles; const mr = m.role; const mrc = ROLE_META[mr];
                  return (
                    <div key={m.id} className="invite-row">
                      {p ? (<>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${mrc?.color}20`, border: `1.5px solid ${mrc?.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: mrc?.color, flexShrink: 0 }}>{p.avatar}</div>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 500 }}>{mr === "client" && project.client_name ? project.client_name : p.name}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{p.email}</div></div>
                        <span style={{ fontSize: 10, color: mrc?.color, background: `${mrc?.color}15`, borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>{mrc?.icon} {ROLE_LABELS[mr]}</span>
                      </>) : (<>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#252420", border: "1.5px solid #2A2924", display: "flex", alignItems: "center", justifyContent: "center", color: "#5A5450", flexShrink: 0 }}>?</div>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: "#7A7468" }}>{m.invited_email}</div><div style={{ fontSize: 10, color: "#5A5450" }}>Invitation en attente</div></div>
                        <span className="badge" style={{ background: "#F6AD5520", color: "#F6AD55", border: "none" }}>En attente</span>
                      </>)}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {tab === "mon-chantier" && role === "contractor" && (() => {
          const myMember = members.find(m => m.user_id === profile.id);
          const myLots = myMember?.lots || [];
          const myTodos = Object.values(todos).flat().filter(t => t.assignee === "contractor" && !t.done);
          const urgentTodos = myTodos.filter(t => t.deadline && daysUntil(t.deadline) <= 7);
          const totalHours = timesheets.reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
          return (
            <div>
              <h1 className="st">{t("myProject")}</h1>
              <div className="stats-grid" style={{ marginBottom: 20 }}>
                {[{ label: "Mes lots", value: myLots.length > 0 ? myLots.length : "—", color: "#A8C97E" }, { label: "Actions urgentes", value: urgentTodos.length, color: urgentTodos.length > 0 ? "#FC8181" : "#E8E2D8" }, { label: "Heures pointées", value: `${Math.round(totalHours)}h`, color: "#7EB8C9" }, { label: "Réserves ouvertes", value: reserves.filter(r => !r.resolved).length, color: reserves.filter(r => !r.resolved).length > 0 ? "#F6AD55" : "#E8E2D8" }].map((s, i) => (
                  <div key={i} className="card" style={{ padding: 16 }}><div className="sl" style={{ marginBottom: 8 }}>{s.label}</div><div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, color: s.color }}>{s.value}</div></div>
                ))}
              </div>
              <div className="card" style={{ padding: 18, marginBottom: 16 }}>
                <div className="sl" style={{ marginBottom: 12 }}>Mes lots assignés</div>
                {myLots.length === 0 ? <div style={{ fontSize: 12, color: "#5A5450", textAlign: "center", padding: "16px 0" }}>Aucun lot assigné — contactez votre architecte</div> : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {myLots.map(lot => <span key={lot} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, background: "#A8C97E20", color: "#A8C97E", border: "1px solid #A8C97E40", fontWeight: 500 }}>{lot}</span>)}
                  </div>
                )}
              </div>
              {urgentTodos.length > 0 && (
                <div className="card" style={{ padding: 18, marginBottom: 16, border: "1px solid #FC818130" }}>
                  <div className="sl" style={{ marginBottom: 12, color: "#FC8181" }}>⚠ Décisions en attente — impact sur mon planning</div>
                  {urgentTodos.map(todo => {
                    const days = daysUntil(todo.deadline);
                    return (
                      <div key={todo.id} style={{ padding: "10px 13px", background: "#252420", borderRadius: 9, marginBottom: 8, display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, color: "#E8E2D8", marginBottom: 3 }}>{todo.text}</div>{todo.impact && <div style={{ fontSize: 11, color: "#FC8181" }}>⚠ {todo.impact}</div>}</div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: days <= 0 ? "#FC8181" : "#F6AD55", flexShrink: 0 }}>{days <= 0 ? `${Math.abs(days)}j retard` : `J-${days}`}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {myTodos.length > 0 && (
                <div className="card" style={{ padding: 18, marginBottom: 16 }}>
                  <div className="sl" style={{ marginBottom: 12 }}>Toutes mes actions</div>
                  {myTodos.map(todo => {
                    const days = todo.deadline ? daysUntil(todo.deadline) : null;
                    const dlColor = days === null ? "#5A5450" : days < 0 ? "#FC8181" : days <= 7 ? "#F6AD55" : "#A8C97E";
                    return (
                      <div key={todo.id} style={{ padding: "10px 13px", background: "#252420", borderRadius: 9, marginBottom: 8, display: "flex", gap: 12, alignItems: "center" }}>
                        <div style={{ flex: 1 }}><div style={{ fontSize: 13, color: "#E8E2D8" }}>{todo.text}</div>{todo.impact && <div style={{ fontSize: 11, color: "#7A6848", marginTop: 2 }}>⚠ {todo.impact}</div>}</div>
                        {todo.deadline && <span style={{ fontSize: 11, fontWeight: 600, color: dlColor, flexShrink: 0 }}>📅 {days !== null && days < 0 ? `${Math.abs(days)}j retard` : days === 0 ? "Auj." : `J-${days}`}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="card" style={{ padding: 18, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div className="sl">Pointage des heures</div>
                  <span style={{ fontSize: 11, color: "#7EB8C9" }}>Total : {Math.round(totalHours)}h</span>
                </div>
                {timesheets.map(h => (
                  <div key={h.id} style={{ padding: "8px 13px", background: "#252420", borderRadius: 9, marginBottom: 7, display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 13, color: "#E8E2D8", fontWeight: 500 }}>{h.employee}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{h.date}{h.lot ? ` · ${h.lot}` : ""}</div></div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#7EB8C9" }}>{h.hours}h</span>
                  </div>
                ))}
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={newEmployee} onChange={e => setNewEmployee(e.target.value)} placeholder="Nom de l'employé" style={{ flex: 2, fontSize: 12 }} />
                    <input type="number" value={newHours} onChange={e => setNewHours(e.target.value)} placeholder="Heures" style={{ flex: 1, fontSize: 12 }} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="date" value={newHoursDate} onChange={e => setNewHoursDate(e.target.value)} style={{ flex: 1, fontSize: 12 }} />
                    <input value={newHoursLot} onChange={e => setNewHoursLot(e.target.value)} placeholder="Lot (optionnel)" style={{ flex: 1, fontSize: 12 }} />
                  </div>
                  <button className="btn" onClick={() => { if (!newEmployee.trim() || !newHours) return; const entry = { id: Date.now(), employee: newEmployee.trim(), hours: parseFloat(newHours), date: newHoursDate, lot: newHoursLot || null }; updateTimesheets([...timesheets, entry]); setNewEmployee(""); setNewHours(""); setNewHoursLot(""); showToast("✓ Heures pointées"); }} style={{ background: "#7EB8C9", color: "#0F0E0C", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, border: "none" }}>
                    + Pointer les heures
                  </button>
                </div>
              </div>
              <div className="card" style={{ padding: 18, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div className="sl">{t("reservesTitle")}</div>
                  <span style={{ fontSize: 11, color: "#F6AD55" }}>{reserves.filter(r => !r.resolved).length} ouvertes</span>
                </div>
                {reserves.map(r => (
                  <div key={r.id} style={{ padding: "10px 13px", background: "#252420", borderRadius: 9, marginBottom: 8, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 3 }}>
                        <div style={{ fontSize: 13, color: r.resolved ? "#5A5450" : "#E8E2D8", textDecoration: r.resolved ? "line-through" : "none" }}>{r.description || r.text}</div>
                        {r.severity && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: r.severity === "high" ? "#FC818120" : r.severity === "medium" ? "#F6AD5520" : "#A8C97E20", color: r.severity === "high" ? "#FC8181" : r.severity === "medium" ? "#F6AD55" : "#A8C97E", fontWeight: 600 }}>{r.severity === "high" ? "Bloquant" : r.severity === "medium" ? t("sevModerate") : t("sevMinor")}</span>}
                      </div>
                      {r.location && <div style={{ fontSize: 10, color: "#5A5450", marginTop: 2 }}>📍 {r.location}</div>}
                      <div style={{ fontSize: 10, color: "#5A5450", marginTop: 2 }}>{r.date}</div>
                      {r.photos && r.photos.length > 0 && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                          {r.photos.map((url, i) => (
                            <img key={i} src={url} alt="" onClick={() => window.open(url, "_blank")}
                              style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 7, cursor: "pointer", border: "1px solid #2A2924" }} />
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="btn" onClick={() => leverReserve(r.id)} style={{ fontSize: 10, color: r.resolved ? "#5A5450" : "#A8C97E", border: `1px solid ${r.resolved ? "#2A2924" : "#A8C97E40"}`, borderRadius: 6, padding: "3px 9px", flexShrink: 0 }}>
                      {r.resolved ? t("reopen") : t("resolve")}
                    </button>
                  </div>
                ))}
                <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                  <input value={newReserve} onChange={e => setNewReserve(e.target.value)} placeholder="Description de la réserve..." style={{ fontSize: 12 }} />
                  <input value={newResLoc} onChange={e => setNewResLoc(e.target.value)} placeholder="Localisation (ex: SDB - mur nord)" style={{ fontSize: 12 }} />
                  <select value={newResSev} onChange={e => setNewResSev(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="low">Mineure</option>
                    <option value="medium">Modérée</option>
                    <option value="high">Bloquante</option>
                  </select>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 13px", background: "#1A1916", borderRadius: 8, border: `1px solid ${newResPhotos.length > 0 ? "#7EB8C940" : "#2A2924"}`, fontSize: 12, color: uploadingPhoto ? "#7A7468" : newResPhotos.length > 0 ? "#7EB8C9" : "#5A5450", cursor: uploadingPhoto ? "default" : "pointer" }}>
                    📷 {uploadingPhoto ? "Envoi en cours..." : newResPhotos.length > 0 ? `${newResPhotos.length} photo${newResPhotos.length > 1 ? "s" : ""} ajoutée${newResPhotos.length > 1 ? "s" : ""}` : "Ajouter des photos (optionnel)"}
                    <input type="file" accept="image/*" multiple capture="environment" disabled={uploadingPhoto} style={{ display: "none" }}
                      onChange={async (e) => {
                        const files = Array.from(e.target.files);
                        for (const f of files) {
                          const url = await uploadReservePhoto(f);
                          if (url) setNewResPhotos(prev => [...prev, url]);
                        }
                      }} />
                  </label>
                  {newResPhotos.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {newResPhotos.map((url, i) => (
                        <div key={i} style={{ position: "relative" }}>
                          <img src={url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 7, border: "1px solid #2A2924" }} />
                          <button className="btn" onClick={() => setNewResPhotos(prev => prev.filter((_, j) => j !== i))}
                            style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: "#FC8181", color: "#0F0E0C", fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", border: "none", padding: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button className="btn" onClick={handleDeclareReserve} style={{ background: "#F6AD55", color: "#0F0E0C", borderRadius: 8, padding: 10, fontSize: 13, fontWeight: 600, border: "none" }}>
                    + Déclarer une réserve
                  </button>
                </div>
              </div>
              <div className="card" style={{ padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div className="sl">Demandes d'approvisionnement</div>
                  <span style={{ fontSize: 11, color: "#5A5450" }}>{supplies.filter(s => s.status === "pending").length} en attente</span>
                </div>
                {supplies.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#252420", borderRadius: 8, marginBottom: 8, fontSize: 12 }}>
                    <div style={{ flex: 1 }}><div style={{ fontWeight: 500 }}>{s.name || s.description}</div><div style={{ fontSize: 10, color: "#5A5450" }}>{s.date}</div></div>
                    {s.amount > 0 && <span style={{ color: "#C9A96E", fontWeight: 600, flexShrink: 0 }}>{formatEur(s.amount)}</span>}
                    <span className="badge" style={{ background: s.status === "approved" ? "#A8C97E20" : "#F6AD5520", color: s.status === "approved" ? "#A8C97E" : "#F6AD55", border: "none", flexShrink: 0 }}>{s.status === "approved" ? "Approuvé" : "En attente"}</span>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <input value={newSupplyDesc} onChange={e => setNewSupplyDesc(e.target.value)} placeholder="Matériau / fourniture..." style={{ flex: 2, fontSize: 12 }} />
                  <input type="number" value={newSupplyAmt} onChange={e => setNewSupplyAmt(e.target.value)} placeholder="€" style={{ width: 70, fontSize: 12 }} />
                  <button className="btn" onClick={() => { if (!newSupplyDesc.trim()) return; const entry = { id: Date.now(), description: newSupplyDesc.trim(), amount: parseFloat(newSupplyAmt) || 0, date: new Date().toLocaleDateString("fr-FR"), status: "pending" }; updateSupplies([...supplies, entry]); setNewSupplyDesc(""); setNewSupplyAmt(""); showToast("✓ Demande envoyée"); }} style={{ background: "#A8C97E", color: "#0F0E0C", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, border: "none", flexShrink: 0 }}>+</button>
                </div>
              </div>
            </div>
          );
        })()}

        {tab === "inspiration" && role === "client" && (
          <PinterestInspiration projectId={project.id} budget={budget} updateBudget={updateBudget} materials={materials} updateMaterials={updateMaterials} journal={journal} updateJournal={updateJournal} profile={profile} NOW={NOW} />
        )}

        {tab === "calendar" && (
          <div>
            <h1 className="st">Calendrier</h1>
            <CalendarView
              ganttBlocks={project.gantt_data || []}
              projectStartDate={project.created_at}
            />
          </div>
        )}

        {tab === "gantt" && (
          <div>
            <h1 className="st">Planning</h1>
            <GanttChart
              key={project.id}
              initialBlocks={project.gantt_data || []}
              deadline={project.deadline ? daysUntil(project.deadline) : undefined}
              readOnly={role === "client"}
              projectName={project.name}
              onSave={async (blocks) => {
                await supabase.from("projects").update({ gantt_data: blocks }).eq("id", project.id);
              }}
            />
          </div>
        )}

      </main>

      <footer style={{ borderTop: "1px solid #1F1E1A", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${rm.color}20`, border: `1.5px solid ${rm.color}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: rm.color }}>{profile.avatar}</div>
          <span style={{ fontSize: 11, color: "#7A7468" }}>{profile.name}</span>
          <span style={{ fontSize: 10, color: rm.color, background: `${rm.color}15`, borderRadius: 4, padding: "2px 7px", fontWeight: 600 }}>{rm.icon} {ROLE_LABELS[role]}</span>
        </div>
        <button onClick={onBack} className="btn" style={{ background: "none", border: "1px solid #2A2924", borderRadius: 8, padding: "5px 12px", fontSize: 11, color: "#5A5450" }}>← Projets</button>
      </footer>

      {/* BOTTOM NAV — mobile only */}
      <BottomNav tab={tab} setTab={setTab} role={role} urgentCnt={urgentCnt} unreadNotifs={unreadNotifs.length} />

      {/* TOAST */}
      {toast && <div className="toast">{toast}</div>}

      {/* CONFIRM MODAL */}
      {confirm && <ConfirmModal title={confirm.title} message={confirm.message} confirmLabel={confirm.confirmLabel} danger={confirm.danger} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}

      {/* MODAL: Ajouter matériau */}
      {showAddMat && (
        <div className="modal-overlay" onClick={() => setShowAddMat(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>Nouveau matériau</span>
              <button className="btn" onClick={() => setShowAddMat(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>Nom *</div><input placeholder="ex: Carrelage 60x60 gris anthracite" value={newMatLabel} onChange={e => setNewMatLabel(e.target.value)} autoFocus /></div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}><div className="sl" style={{ marginBottom: 6 }}>Prix €</div><input type="number" placeholder="0" value={newMatPrice} onChange={e => setNewMatPrice(e.target.value)} /></div>
                <div style={{ flex: 2 }}><div className="sl" style={{ marginBottom: 6 }}>Corps de métier</div><input placeholder="ex: Carrelage, Plomberie..." value={newMatLot} onChange={e => setNewMatLot(e.target.value)} /></div>
              </div>
              <div><div className="sl" style={{ marginBottom: 6 }}>URL fournisseur (optionnel)</div><input placeholder="https://..." value={newMatUrl} onChange={e => setNewMatUrl(e.target.value)} /></div>
              <button onClick={() => {
                if (!newMatLabel.trim()) return;
                updateMaterials({ ...materials, [activeRoom]: [...(materials[activeRoom] || []), { id: Date.now(), label: newMatLabel.trim(), tag: "À valider", color: "#2A2924", price: parseFloat(newMatPrice) || 0, validated: false, addedBy: role, addedAt: NOW, supplierUrl: newMatUrl || null, lot: newMatLot || null, history: [{ action: "Proposé", by: role, at: NOW }] }] });
                setShowAddMat(false); setNewMatLabel(""); setNewMatPrice(""); setNewMatUrl(""); setNewMatLot("");
                showToast("✓ Matériau ajouté");
              }} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}>Ajouter →</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nouvelle situation de paiement */}
      {showAddSituation && (
        <div className="modal-overlay" onClick={() => setShowAddSituation(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>Nouvelle situation</span>
              <button className="btn" onClick={() => setShowAddSituation(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>Description *</div><input placeholder="ex: Situation n°1 — Démolition" value={newSitDesc} onChange={e => setNewSitDesc(e.target.value)} autoFocus /></div>
              <div><div className="sl" style={{ marginBottom: 6 }}>Montant TTC €</div><input type="number" placeholder="0" value={newSitAmt} onChange={e => setNewSitAmt(e.target.value)} /></div>
              <button onClick={() => {
                if (!newSitDesc.trim()) return;
                updateBudget({ ...budget, paymentSituations: [...(budget.paymentSituations || []), { id: Date.now(), date: new Date().toLocaleDateString("fr-FR"), description: newSitDesc.trim(), amount: parseFloat(newSitAmt) || 0, status: "pending" }] });
                setShowAddSituation(false); setNewSitDesc(""); setNewSitAmt("");
                showToast("✓ Situation ajoutée");
              }} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}>Créer →</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Ajouter un lot */}
      {showAddLot && (
        <div className="modal-overlay" onClick={() => setShowAddLot(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>Nouveau lot</span>
              <button className="btn" onClick={() => setShowAddLot(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>Nom du lot *</div><input placeholder="ex: Plomberie, Électricité, Carrelage..." value={newLotName} onChange={e => setNewLotName(e.target.value)} autoFocus /></div>
              <button onClick={() => {
                if (!newLotName.trim()) return;
                setMembers(prev => prev.map(pm => pm.id === addLotMemberId ? { ...pm, lots: [...(pm.lots || []), newLotName.trim()] } : pm));
                setShowAddLot(false); setNewLotName(""); setAddLotMemberId(null);
                showToast("✓ Lot assigné");
              }} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}>Assigner →</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Inviter un membre */}
      {showInvite && (
        <div className="modal-overlay" onClick={() => setShowInvite(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #2A2924", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: "#E8E2D8" }}>Inviter un membre</span>
              <button className="btn" onClick={() => setShowInvite(false)} style={{ fontSize: 18, color: "#5A5450" }}>✕</button>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div><div className="sl" style={{ marginBottom: 6 }}>Email</div><input type="email" placeholder="client@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} /></div>
              <div>
                <div className="sl" style={{ marginBottom: 8 }}>Rôle sur le projet</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ v: "client", l: "◆ Client" }, { v: "contractor", l: "● Entreprise" }, { v: "architect", l: "▲ Architecte" }].map(r => (
                    <button key={r.v} onClick={() => setInviteRole(r.v)} className="btn"
                      style={{ flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 11, fontWeight: inviteRole === r.v ? 600 : 400, background: inviteRole === r.v ? `${ROLE_META[r.v].color}20` : "#252420", border: `1px solid ${inviteRole === r.v ? ROLE_META[r.v].color + "60" : "#2A2924"}`, color: inviteRole === r.v ? ROLE_META[r.v].color : "#7A7468" }}>
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={inviteMember} className="btn" style={{ background: "#C9A96E", color: "#0F0E0C", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, border: "none" }}>Inviter →</button>
              <div style={{ textAlign: "center" }}>
                <div className="sl" style={{ marginBottom: 8 }}>Ou partagez le code</div>
                <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: "#C9A96E", letterSpacing: "0.2em" }}>{project.join_code}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
